import React from 'react';
import { X, Database, FileJson, AlertCircle } from 'lucide-react';
import { AgentStepDetail } from '../types';

interface AgentDetailModalProps {
  step: AgentStepDetail | null;
  isOpen: boolean;
  onClose: () => void;
}

export function AgentDetailModal({ step, isOpen, onClose }: AgentDetailModalProps) {
  if (!isOpen || !step) return null;

  const hasError = !!step.error;
  const hasData = Array.isArray(step.data) && step.data.length > 0;
  const hasQuery = step.query && Object.keys(step.query).length > 0;

  // Get columns from first data row for table header
  const firstRow = step.data && step.data.length > 0 ? step.data[0] : null;
  const columns = firstRow ? Object.keys(firstRow) : [];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        data-testid="agent-detail-backdrop"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 text-blue-600">
              <Database className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 capitalize">
                {step.type.replace('_', ' ')}
              </h3>
              <p className="text-sm text-gray-500">{step.instruction}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            data-testid="agent-detail-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 space-y-6">
          {/* Error Banner */}
          {hasError && (
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <p className="font-medium text-red-900">Execution Error</p>
                <p className="text-sm text-red-700">{step.error}</p>
              </div>
            </div>
          )}

          {/* Query Section */}
          {hasQuery && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <FileJson className="w-4 h-4 text-gray-500" />
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Generated Query
                </h4>
              </div>
              <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                <pre className="p-4 text-xs font-mono text-gray-700 overflow-x-auto">
                  {JSON.stringify(step.query, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* Data Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-gray-500" />
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Returned Data
                </h4>
              </div>
              <div className="text-sm text-gray-500">
                {step.rowCount ?? step.data?.length ?? 0} rows
                {step.totalCount && step.totalCount > (step.rowCount ?? 0) && (
                  <span className="text-amber-600 ml-1">
                    (truncated from {step.totalCount})
                  </span>
                )}
              </div>
            </div>

            {hasData ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-[400px]">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        {columns.map((col) => (
                          <th
                            key={col}
                            className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200"
                          >
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {step.data?.map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          {columns.map((col) => (
                            <td
                              key={`${idx}-${col}`}
                              className="px-3 py-2 text-gray-700 whitespace-nowrap"
                            >
                              {typeof row[col] === 'object'
                                ? JSON.stringify(row[col])
                                : String(row[col] ?? '')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                No data returned
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
